/**
 * FilterPill Component
 *
 * Unified component for quick filters in toolbars.
 */

import type { ReactNode } from 'react';
import clsx from 'clsx';

export interface FilterPillProps {
  icon: ReactNode;
  label: string;
  count?: number;
  isActive: boolean;
  onClick: () => void;
  color?: 'default' | 'rose' | 'blue' | 'emerald' | 'violet' | 'amber';
  size?: 'sm' | 'md';
}

export function FilterPill({
  icon,
  label,
  count,
  isActive,
  onClick,
  color = 'default',
  size = 'md',
}: FilterPillProps) {
  const colorClasses: Record<string, string> = {
    default: 'text-text-tertiary',
    rose: 'text-accent-rose',
    blue: 'text-accent-blue',
    emerald: 'text-accent-emerald',
    violet: 'text-accent-violet',
    amber: 'text-accent-amber',
  };

  return (
    <button
      onClick={onClick}
      className={clsx(
        'flex items-center gap-2 rounded-lg font-medium transition-all border',
        size === 'sm' ? 'px-2.5 py-1.5 text-[10px]' : 'px-3 py-2 text-[11px]',
        isActive
          ? 'bg-accent-violet text-white border-accent-violet shadow-sm'
          : 'bg-surface-tertiary/50 border-border-primary hover:border-border-secondary text-text-tertiary hover:text-text-secondary hover:bg-surface-hover'
      )}
    >
      <span className={clsx(!isActive && color !== 'default' && colorClasses[color])}>{icon}</span>
      <span>{label}</span>
      {count !== undefined && (
        <span
          className={clsx(
            'px-1.5 py-0.5 rounded-full font-semibold tabular-nums',
            size === 'sm' ? 'text-[9px]' : 'text-[10px]',
            isActive ? 'bg-white/20' : 'bg-surface-tertiary'
          )}
        >
          {count}
        </span>
      )}
    </button>
  );
}
